import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSingleFloorPlan } from "../../../utils/fetchSingleFloorPlan";
import { computeQuantityDiff } from "../../utils/computeQuantityDiff";
import { planComparisonDataKeys } from "./planComparisonDataKeys";
import type {
  UsePlanComparisonDataArgs,
  UsePlanComparisonDataResult,
} from "./types";

// Must not vary by side — the cache is keyed by plan identity, not by role.
const UNUSED_PLAN_INDEX = 0;

// Long enough to survive a tab switch, short enough to pick up a plan edit.
const PLAN_STALE_TIME_MS = 60_000;

export const usePlanComparisonData = ({
  beforePlanRecord,
  afterPlanRecord,
}: UsePlanComparisonDataArgs): UsePlanComparisonDataResult => {
  const beforeQuery = useQuery({
    queryKey: planComparisonDataKeys.plan(beforePlanRecord),
    queryFn: () =>
      fetchSingleFloorPlan({
        record: beforePlanRecord,
        index: UNUSED_PLAN_INDEX,
      }),
    staleTime: PLAN_STALE_TIME_MS,
  });

  const afterQuery = useQuery({
    queryKey: planComparisonDataKeys.plan(afterPlanRecord),
    queryFn: () =>
      fetchSingleFloorPlan({
        record: afterPlanRecord,
        index: UNUSED_PLAN_INDEX,
      }),
    staleTime: PLAN_STALE_TIME_MS,
  });

  const beforePlan = beforeQuery.data ?? null;
  const afterPlan = afterQuery.data ?? null;

  const quantityDiff = useMemo(() => {
    if (beforePlan === null || afterPlan === null) return null;
    return computeQuantityDiff({ beforePlan, afterPlan });
  }, [beforePlan, afterPlan]);

  return {
    beforePlan,
    afterPlan,
    quantityDiff,
    isLoading: beforeQuery.isPending || afterQuery.isPending,
    isError: beforeQuery.isError || afterQuery.isError,
  };
};
